import { useEffect, useState, useMemo } from 'react'
import ApiService from '../../common/ApiCalls'

const assignmentLabels = {
  'Industry Types': 'entities',
  'Entity Types': 'entities',
  'Service Types': 'services',
  'Service Categories': 'services',
  'Contact Categories': 'people',
  'Job Titles': 'people'
}

const getDeletionWarning = (referenceType, item, activeAssignments) =>
  `${item.name} is currently assigned to ${activeAssignments} active ${assignmentLabels[referenceType] || 'records'}. ` +
  'Deleting it will remove it from future selections while preserving it on existing records. ' +
  'Would you like to continue?'

const useReferenceDataConfiguration = (onEditMode, setOnEditMode, setIsLoading, setIsSnackbarOpen) => {
  const [selectedReferenceType, setSelectedReferenceType] = useState('Industry Types')
  const [items, setItems] = useState([])
  const [itemsOriginal, setItemsOriginal] = useState([])
  const [selectedRow, setSelectedRow] = useState(null)

  const strategies = useMemo(() => ({
    'Industry Types': {
      fetchData: async () => {
        const data = await ApiService().getIndustries({ includeArchived: true })
        return data
      },
      createEntity: async (item) => {
        return await ApiService().createIndustry({
          name: item.name,
          archived_at: item.archived_at
        })
      },
      updateEntity: async (item) => {
        return await ApiService().updateIndustry(item.id, {
          name: item.name,
          archived_at: item.archived_at
        })
      },
      deleteEntity: async (item) => {
        return await ApiService().deleteIndustry(item.id)
      },
      getDeletionImpact: async (item) => {
        return await ApiService().getIndustryDeletionImpact(item.id)
      }
    },
    'Entity Types': {
      fetchData: async () => {
        const data = await ApiService().getEntitiesTypes({ includeArchived: true })
        return data
      },
      createEntity: async (item) => {
        return await ApiService().createEntityType({
          name: item.name,
          archived_at: item.archived_at || null
        })
      },
      updateEntity: async (item) => {
        return await ApiService().updateEntityType(item.id, {
          name: item.name,
          archived_at: item.archived_at || null
        })
      },
      deleteEntity: async (item) => {
        return await ApiService().deleteEntityType(item.id)
      },
      getDeletionImpact: async (item) => {
        return await ApiService().getEntityTypeDeletionImpact(item.id)
      }
    },
    'Service Types': {
      fetchData: async () => {
        const data = await ApiService().listServiceTypes({ includeArchived: true })
        return data
      },
      createEntity: async (item) => {
        return await ApiService().createServiceType({
          name: item.name,
          archived_at: item.archived_at
        })
      },
      updateEntity: async (item) => {
        return await ApiService().updateServiceType(item.id, {
          name: item.name,
          archived_at: item.archived_at
        })
      },
      deleteEntity: async (item) => {
        return await ApiService().deleteServiceType(item.id)
      },
      getDeletionImpact: async (item) => {
        return await ApiService().getServiceTypeDeletionImpact(item.id)
      }
    },
    'Service Categories': {
      fetchData: async () => {
        const data = await ApiService().listServiceCategories({ includeArchived: true })
        return data
      },
      createEntity: async (item) => {
        return await ApiService().createServiceCategory({
          name: item.name,
          archived_at: item.archived_at
        })
      },
      updateEntity: async (item) => {
        return await ApiService().updateServiceCategory(item.id, {
          name: item.name,
          archived_at: item.archived_at
        })
      },
      deleteEntity: async (item) => {
        return await ApiService().deleteServiceCategory(item.id)
      },
      getDeletionImpact: async (item) => {
        return await ApiService().getServiceCategoryDeletionImpact(item.id)
      }
    },
    'Contact Categories': {
      fetchData: async () => {
        const data = await ApiService().listContactTypes({ includeArchived: true })
        return data
      },
      createEntity: async (item) => {
        return await ApiService().createContactCategory({
          name: item.name,
          archived_at: item.archived_at
        })
      },
      updateEntity: async (item) => {
        return await ApiService().updateContactCategory(item.id, {
          name: item.name,
          archived_at: item.archived_at
        })
      },
      deleteEntity: async (item) => {
        return await ApiService().deleteContactCategory(item.id)
      },
      getDeletionImpact: async (item) => {
        return await ApiService().getContactCategoryDeletionImpact(item.id)
      }
    },
    'Job Titles': {
      fetchData: async () => {
        const data = await ApiService().listJobTitles({ includeArchived: true })
        return data
      },
      createEntity: async (item) => {
        return await ApiService().createJobTitle({
          name: item.name,
          archived_at: item.archived_at
        })
      },
      updateEntity: async (item) => {
        return await ApiService().updateJobTitle(item.id, {
          name: item.name,
          archived_at: item.archived_at
        })
      },
      deleteEntity: async (item) => {
        return await ApiService().deleteJobTitle(item.id)
      },
      getDeletionImpact: async (item) => {
        return await ApiService().getJobTitleDeletionImpact(item.id)
      }
    },
  }), [])

  useEffect(() => {
    fetchData(selectedReferenceType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReferenceType, onEditMode])

  const fetchData = async (type) => {
    setIsLoading(true)
    try {
      const strategy = strategies[type]
      let data = []

      if (strategy) {
        data = await strategy.fetchData()
      }

      setItems(data)
      setItemsOriginal(JSON.parse(JSON.stringify(data)))
    } catch (error) {
      console.error(`Error fetching ${type}:`, error)
      setIsSnackbarOpen({
        isOpen: true,
        severity: 'error',
        message: `Failed to load ${type}. Please try again.`
      })
    } finally {
      setIsLoading(false)
    }
  }

  const performEntityOperation = async (operation, item) => {
    try {
      const strategy = strategies[selectedReferenceType]
      if (strategy && strategy[operation]) {
        return await strategy[operation](item)
      }
    } catch (error) {
      console.error(`Error during ${operation}:`, error)
      throw error
    }
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      const itemsToCreate = items.filter((item) => !item.id)
      const itemsToUpdate = items.filter((item) => {
        const originalItem = itemsOriginal.find((orig) => orig.id === item.id)
        return (
          originalItem &&
          (item.name !== originalItem.name || item.archived_at !== originalItem.archived_at)
        )
      })
      const itemsToDelete = itemsOriginal.filter((item) => !items.find((i) => i.id === item.id))

      // Process all operations with proper error handling
      const operations = [
        ...itemsToCreate.map(item => performEntityOperation('createEntity', item)),
        ...itemsToUpdate.map(item => performEntityOperation('updateEntity', item)),
        ...itemsToDelete.map(item => performEntityOperation('deleteEntity', item))
      ]

      await Promise.all(operations)

      setIsSnackbarOpen({ isOpen: true, severity: 'success', message: 'Information successfully updated' })
      setOnEditMode(false)
      fetchData(selectedReferenceType)
    } catch (error) {
      console.error('Error saving changes:', error)
      setIsSnackbarOpen({
        isOpen: true,
        severity: 'error',
        message: 'Failed to save changes. Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setOnEditMode(false)
    setItems(JSON.parse(JSON.stringify(itemsOriginal)))
  }

  const addItem = () => {
    setItems([...items, { name: '', archived_at: null }])
  }

  const updateItem = (index, updatedItem) => {
    const newItems = [...items]
    newItems[index] = updatedItem
    setItems(newItems)
  }

  const deleteItem = async (index) => {
    const item = items[index]

    if (item.id) {
      setIsLoading(true)
      try {
        const impact = await performEntityOperation('getDeletionImpact', item)
        const rawActiveAssignments = impact?.data?.active_assignments
        const activeAssignments = Number(rawActiveAssignments)

        if (
          rawActiveAssignments === null ||
          rawActiveAssignments === undefined ||
          !Number.isInteger(activeAssignments) ||
          activeAssignments < 0
        ) {
          throw new Error('Invalid deletion impact response')
        }

        if (
          activeAssignments > 0 &&
          !window.confirm(
            getDeletionWarning(selectedReferenceType, item, activeAssignments)
          )
        ) return
      } catch (error) {
        console.error('Error checking reference data assignments:', error)
        setIsSnackbarOpen({
          isOpen: true,
          severity: 'error',
          message: 'Unable to check whether this reference is in use. Please try again.'
        })
        return
      } finally {
        setIsLoading(false)
      }
    }

    setItems(currentItems => currentItems.filter((_, itemIndex) => itemIndex !== index))
  }

  return {
    selectedReferenceType,
    setSelectedReferenceType,
    items,
    setItems,
    handleSave,
    handleCancel,
    addItem,
    updateItem,
    deleteItem,
    selectedRow,
    setSelectedRow,
  }
}

export default useReferenceDataConfiguration
